import Search from "@mui/icons-material/Search";
import {
  InputAdornment,
  OutlinedInput,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import Fuse from "fuse.js";
import { isString, sortBy } from "lodash-es";
import { Fragment, useCallback, useMemo, useState } from "react";
import { Scrollbar } from "../scrollbar";

import type { ReactNode } from "react";

const DEFAULT_PAGE_SIZE = 25;

interface PaginatedTableProps<T extends { [key: string]: unknown }> {
  data: T[];
  renderRow: (row: T) => ReactNode;
  config?: {
    headers?: string[];
    defaultRowsPerPage?: number;
    search?: {
      keys: (keyof T)[];
      threshold: number;
    };
    sort?: {
      default: {
        column: keyof T;
        direction: "asc" | "desc";
      };
      options: { value: keyof T; label: string }[];
    };
  };
}

export function PaginatedTable<T extends { [key: string]: unknown }>({
  data,
  renderRow,
  config,
}: PaginatedTableProps<T>): ReactNode {
  const [search, setSearch] = useState("");

  const [sort, setSort] = useState<{
    column: keyof T;
    direction: "asc" | "desc";
  } | null>(config?.sort?.default ?? null);

  const [tableState, setTableState] = useState({
    page: 0,
    rowsPerPage: config?.defaultRowsPerPage ?? DEFAULT_PAGE_SIZE,
  });

  const sortOptions = useMemo(
    () =>
      config?.sort?.options
        .filter((option) => isString(option.value))
        .flatMap((option) => [
          { value: `${option.value.toString()}|asc`, label: `${option.label} (A-Z)` },
          { value: `${option.value.toString()}|desc`, label: `${option.label} (Z-A)` },
        ]) ?? [],
    [config?.sort?.options],
  );

  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const [column, direction] = e.target.value.split("|");
      setSort({
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        column: column as keyof T,
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        direction: direction as "asc" | "desc",
      });
    },
    [setSort],
  );

  const configSearch = config?.search;
  const configSearchKeys = configSearch?.keys;
  const configSearchThreshold = configSearch?.threshold;

  const fuse = useMemo(
    () =>
      configSearch
        ? new Fuse(data, {
            keys: configSearchKeys?.filter(isString),
            threshold: configSearchThreshold,
          })
        : null,
    [configSearch, data, configSearchKeys, configSearchThreshold],
  );

  const filteredData = useMemo(() => {
    const filtered = search !== "" ? fuse?.search(search).map((result) => result.item) : data;

    return filtered ?? [];
  }, [data, fuse, search]);

  const pagedAndSortedData = useMemo(() => {
    const sorted = sort ? sortBy(filteredData, (row) => row[sort.column]) : (filteredData ?? []);

    if (sort?.direction === "desc") sorted.reverse();

    return sorted.slice(
      tableState.page * (tableState.rowsPerPage ?? DEFAULT_PAGE_SIZE),
      (tableState.page + 1) * (tableState.rowsPerPage ?? DEFAULT_PAGE_SIZE),
    );
  }, [filteredData, sort, tableState.page, tableState.rowsPerPage]);

  return (
    <Stack direction="column" spacing={2}>
      {(fuse || sort) && (
        <Stack alignItems="center" direction="row" spacing={3} sx={{ p: 3 }}>
          {fuse && (
            <OutlinedInput
              value={search}
              sx={{ flexGrow: 1 }}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              inputProps={{ "data-testid": "peopleFilter" }}
              placeholder="Search"
              startAdornment={
                <InputAdornment position="start">
                  <SvgIcon>
                    <Search />
                  </SvgIcon>
                </InputAdornment>
              }
            />
          )}
          {sort && isString(sort.column) && (
            <TextField
              label="Sort By"
              name="sort"
              onChange={handleSortChange}
              select
              slotProps={{ select: { native: true } }}
              value={`${sort.column}|${sort.direction}`}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          )}
        </Stack>
      )}
      <Scrollbar>
        <Table sx={{ minWidth: 700 }}>
          {config?.headers && (
            <TableHead>
              <TableRow>
                {config.headers.map((key) => (
                  <TableCell key={key}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {pagedAndSortedData.map((row, i) => (
              <Fragment key={i}>{renderRow(row)}</Fragment>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      <TablePagination
        component="div"
        count={filteredData.length}
        onPageChange={(_, page) => setTableState((current) => ({ ...current, page }))}
        onRowsPerPageChange={(e) => {
          setTableState((current) => ({ ...current, rowsPerPage: parseInt(e.target.value) }));
        }}
        page={tableState.page}
        rowsPerPage={tableState.rowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Stack>
  );
}
